import { eventActivityType } from "../../constants/myBlockMonth";
import { convertToDateObject, convertToDateTime } from "../../helpers/dateHelpers";
import { formatNumToTime } from "../../helpers/timeHelpers";

export const addCarryInEvents = (events: any[], carryOutOfWeekEvents: any[], updatedEvents: any[], blockMonthDates: any[]) => {
    let newUniqueId = Math.max(...events.map(event => event.unique_id)) + 1;
    carryOutOfWeekEvents.forEach(event => {
        const sundayIndex = findEventSameWeekSundayIndex(event.hasStartRelease ? event.release[0].start_date : event.start_date, blockMonthDates);
        createCarryInWeekEvent(event, blockMonthDates, updatedEvents, sundayIndex, newUniqueId);
        newUniqueId += 1;
    })
}

/**
 * Checks if an event spans beyond the end of the week.
 * @param event
 * @returns boolean
 */
export const checkCarryOutOfWeekEvent = (event: any) => {
    const eventStartDate = event.hasStartRelease ? convertToDateObject(event.release[0].start_date, '00:00') : convertToDateObject(event.start_date, '00:00');
    const eventEndDate = convertToDateObject(event.end_date, '00:00');
    /**
     * to determine if the end date is a Sunday and the end time is 00:00, 
     * subtract 1 day from the end date,
     * to prepare the ending curve for the event capsule
     */
    if(eventEndDate.getDay() === 0 && event.endTime.split(' ')[0] === '00:00') {
        eventEndDate.setDate(eventEndDate.getDate() - 1);
    }
    const eventDaysSpan = getDaysSpan(eventStartDate, eventEndDate);
    return eventStartDate.getDay() + eventDaysSpan-1 > 6;
}

/**
 * combine all the pairing segments from all the events in pairingList event
 * @param event 
 * @returns 
 */
export const combinePairingSegments = (event: any) => {
    let pairingSegments = [] as any;
    let layoverCount = 0;
    event.pairingDetails.events.forEach(event => {
        event.segments.forEach(segment => {
            if(segment.layover) {
                layoverCount++;
                pairingSegments.push({...segment, layoverCount: layoverCount});
            }
            else pairingSegments.push(segment);
        })
    })
    return pairingSegments; 
}

/**
 * Combine routes of multiple segments into 1 route
 * @param segments 
 */
const combineRoutes = (segments: any[]) => { 

    /* route is a variable to hold the current iterated set of routes, reset to [] when a layover is reached */
    let route = [] as any; 

    /* indexOfRoutesToShow is a variable to set the index within the segments array to show the route */
    let indexOfRoutesToShow = -1; 

    segments.forEach((segment, index) => {
        if(segment.isRoute) {

            /* if this is the first route for a new set, copy the segment.route into the route and set the index to show */
            if(!route.length) {
                route = [...segment.routes];
                indexOfRoutesToShow = index;

            /* if an element already exists in route, push the airporCode and flightNo and 
            delete the routes property of the segment (so that it does not render on the calendar display) */
            } else {
                route.push(segment.routes[1]);
                route.push(segment.routes[2]);
                delete segment.routes;
            }

            /* for the last segment, set the correct segments new route property */
            if(index === segments.length-1 && indexOfRoutesToShow !== -1) segments[indexOfRoutesToShow].routes = [...route];

        /* if segment is a layover, set the new segments.route property and reset the fields */
        } else {
            if(route.length && indexOfRoutesToShow !== -1) {
                segments[indexOfRoutesToShow].routes = [...route];
                route = [];
                indexOfRoutesToShow = -1; 
            }
        }
    })
}

const createCarryInWeekEvent = (event: any, blockMonthDates: any[], events: any[], sundayIndex: number, uniqueId: number) => {
    const sundays: string[] = getSundaySaturdays(blockMonthDates)[0];
    const saturdays: string[] = getSundaySaturdays(blockMonthDates)[1];
    if(sundayIndex < sundays.length-1){
      let carryInEvent = {
        ...event,
        checkIn: {value: 0, deviation: 0},
        start_date: sundays[sundayIndex+1],  
        carryIn: true,
        startDateDaysTime: convertToDateObject(sundays[sundayIndex+1], '00:00'),
        unique_id: uniqueId,
        carryOut: false,
        hideEventId: true
      }
      handleCarryInEventWithRelease(carryInEvent, event, sundays, sundayIndex);
      carryInEvent.carryOut = checkCarryOutOfWeekEvent(carryInEvent);
      if(event.activityType === eventActivityType.PAIRING) {
        carryInEvent = {
          ...carryInEvent,
          pairingSegments: getPairingCarryInSegments(event.pairingSegments, sundays, saturdays, sundayIndex)
        }
      } 
      events.push(carryInEvent)
      if(carryInEvent.carryOut) createCarryInWeekEvent(carryInEvent, blockMonthDates, events, sundayIndex+1, uniqueId+1)
    }
}

const findEventSameWeekSundayIndex = (startDate: string, blockMonthDates: any) => {
    const sundaySaturdays: string[][] = getSundaySaturdays(blockMonthDates);
    const sundays: string[] = sundaySaturdays[0];
    const eventStartDate = convertToDateObject(startDate, '00:00');
    const eventStartDateTime = eventStartDate.getTime();
    let sundayIndex = 0;
    sundays.forEach((sunday, index) => {
    const sundayDateTime = convertToDateTime(sunday, '00:00');
    if(eventStartDateTime >= sundayDateTime) sundayIndex = index; 
    })
    return sundayIndex;
}

const getDaysSpan = (startDate: Date, endDate: Date) => {
    const diffInMs = endDate.getTime() - startDate.getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
    return Math.round(diffInDays)+1; 
}

export const getEventDaySpan = (startDateTime: number, endDateTime: number) => {
    const hours = Math.abs(startDateTime - endDateTime)/3.6e6;
    const days = hours/24;
    return Math.ceil(days); 
}

export const getLeftMargin = (checkInValue) => {
    return checkInValue > 0 ? (checkInValue * 100 / 1440) : 0;
}

export const getSundaySaturdays = (daysArr: any[]) => {
    let sundays: string[] = [];
    let saturdays: string[] = [];
    for (let i = 0; i < daysArr.length; i += 7) {
      if(daysArr[i]) sundays.push(daysArr[i].day);
      if(daysArr[i + 6]) saturdays.push(daysArr[i + 6].day);
    }
    return [sundays, saturdays];
};

const getPairingCarryInSegments = (segments: any[], sundays: string[], saturdays: string[], sundayIndex: number) => {
    if(sundayIndex < sundays.length-1) {
        const endOfWeekSaturdayDate = convertToDateObject(saturdays[sundayIndex], "23:59");
        let updatedSegments = [] as any; 
        let indexOfCarryOutSegment = 0;
        for(let i=0; i<segments.length; i++){
            if(segments[i].endDate.getTime() > endOfWeekSaturdayDate.getTime()) { 
                indexOfCarryOutSegment = i; 
                break
            }
        }
        let carryInSegments = segments.slice(indexOfCarryOutSegment); 
        for(let i=0; i<carryInSegments.length; i++){
            let layoverCount = 0;
            if(!carryInSegments[i].isRoute) layoverCount += 1;
            if(i === 0) {
                updatedSegments.push({
                    ...carryInSegments[i],
                    startDate: convertToDateObject(sundays[sundayIndex+1], "00:00"),
                    segmentLength: getSegmentLength(convertToDateObject(sundays[sundayIndex+1], "00:00"), carryInSegments[i].endDate),
                    hideLayoverInformation: !carryInSegments[i].isRoute ? hideLayoverInformation(convertToDateObject(sundays[sundayIndex+1], "00:00"), carryInSegments[i].endDate, carryInSegments[i].startDate, "carryIn") : null,
                    layoverCount: carryInSegments[i].isRoute ? 0 : layoverCount
                })
            } else {
                updatedSegments.push({
                    ...carryInSegments[i],
                    layoverCount: carryInSegments[i].isRoute ? 0 : layoverCount
                });
            }
        }
        return updatedSegments; 
    }
}

const getSegmentLength = (startDate: Date, endDate: Date) => {
    const diffInHours = Math.abs(startDate.getTime() - endDate.getTime())/3.6e6;
    const minutes = diffInHours * 60;
    return minutes/1440;
}

export const getUpdatedCarryOutOfWeekEvent = (event: any, blockMonthDates: any[]) => {
    const saturdays: string[] = getSundaySaturdays(blockMonthDates)[1];
    const sundayIndex = findEventSameWeekSundayIndex(event.hasStartRelease ? event.release[0].start_date :event.start_date, blockMonthDates);
    if(event.carryOut) {
        if(event.activityType === eventActivityType.PAIRING) {
            return {
                ...event,
                end_date: saturdays[sundayIndex],
                checkOut: {value: 1439, deviation: event.checkOut.deviation},
                pairingSegments: updatePairingCarryOutSegments(event.pairingSegments, saturdays, sundayIndex),
                hideEventId: !event.carryIn && event.hasStartRelease ? convertToDateObject(event.release[0].end_date, "24:00").getTime() >  convertToDateObject(saturdays[sundayIndex], "24:00").getTime() : false
            }
        } else {
            return {
                ...event,
                end_date: saturdays[sundayIndex],
                checkOut: {value: 1439, deviation: event.checkOut.deviation}
            }
        }
    } else return event;
}

const handleCarryInEventWithRelease = (carryInEvent, event, sundays, sundayIndex) => {
    if(carryInEvent.hasStartRelease) {
        carryInEvent.release = [...event.release];
        if(convertToDateTime(carryInEvent.release[0].start_date, "00:00") < convertToDateTime(sundays[sundayIndex+1], '00:00')) {
            if(convertToDateTime(carryInEvent.release[0].end_date, "00:00") < convertToDateTime(sundays[sundayIndex+1], '00:00')) {
                carryInEvent.hasStartRelease = false;  
                if(carryInEvent.release.length === 1) delete carryInEvent.release; 
                else carryInEvent.release = carryInEvent.slice(1);
            }
            else {
                carryInEvent.release[0] = {
                    ...carryInEvent.release[0],
                    start_date: sundays[sundayIndex+1],
                    checkIn: {value: 0, deviation: carryInEvent.release[0].checkIn.deviation},
                }
                carryInEvent.hideEventId = false;
            }
        }
    }
}

const hideLayoverInformation = (startDate: Date, endDate: Date, originalStartEndDate: Date, carryOverType: string) => {
    if(carryOverType === "carryOut") {
        const originalEndDate = originalStartEndDate; 
        const layoverDuration = originalEndDate.getTime() - startDate.getTime(); 
        const layoverToEndOfWeekDuration = endDate.getTime() - startDate.getTime(); 
        return layoverToEndOfWeekDuration/layoverDuration < 0.5; 
    }
    if(carryOverType === "carryIn") {
        const originalStartDate = originalStartEndDate;
        const layoverDuration = endDate.getTime() - originalStartDate.getTime();
        const startOfWeekToEndOfLayoverDuration = endDate.getTime() - startDate.getTime(); 
        return startOfWeekToEndOfLayoverDuration/layoverDuration < 0.5; 
    }
    return false; 
}

export const isWeekend = (day: string) => {
    const weekday = new Date(`${day} 00:00`).toLocaleString("default", { weekday: "short" });
    if(weekday === 'Sun' || weekday === 'Sat') return true;
    return false;
}

/**
 * Map the pairing segments returned from the API into the format needed to be displayed on the Standard Calendar
 * @param event 
 * @param pairingSegments 
 * @returns 
 */
export const mapPairingSegments = (event: any, pairingSegments: any[]) => {
    let startDateRoute;
    let segments = [] as any; 
    pairingSegments.forEach((segment, index) => {

        /* for the first segment */
        if(index === 0) {   

            /* if the event has a release, set the startDate to the events release objects startDate and checkIn value */
            if(event.release) startDateRoute = convertToDateObject(event.release[0].start_date, formatNumToTime(event.release[0].checkIn.value));

            /* else set the startDate to the event objects startDate and checkIn value */
            else startDateRoute = convertToDateObject(event.start_date, formatNumToTime(event.checkIn.value));
        }

        /* for the last segment, set the end date to the event's endDate and checkOut value
        else set the end date to the segment's arrival base and offduty base property */
        const endDateRoute = index === pairingSegments.length - 1 
        ? convertToDateObject(event.end_date, formatNumToTime(event.checkOut.value)) 
        : convertToDateObject(segment.arrival.base, formatNumToTime(segment.offduty.base))

        segments.push({
            isRoute: true,
            deadhead: segment.deadhead,
            routes: [
                segment.departure.airportCode,
                segment.deadhead ? `${segment.carierCode}${segment.fltNo.replace(/^0+/, '')}` : segment.fltNo.replace(/^0+/, ''),
                segment.arrival.airportCode
            ],
            segmentLength: getSegmentLength(startDateRoute, endDateRoute),
            startDate: startDateRoute,
            endDate: endDateRoute,
            showRelease: event.release && index === 0
        })

        /* set the startDateRoute of the next segment to the endDateRoute of the current segment that was just pushed */
        startDateRoute = endDateRoute; 

        /* if this segment has a layover field and this is not the last segment of the event, add the layover segment */
        if(segment.layover && index !== pairingSegments.length -1 ) {
            const layoverEndDate = convertToDateObject(pairingSegments[index+1].departure.base, formatNumToTime(pairingSegments[index+1].onduty.base));
            segments.push({
                isRoute: false,
                layoverLocation: segment.layover,
                layoverCount: segment.layoverCount,
                startDate: endDateRoute,
                endDate: layoverEndDate,
                layoverStartTime: formatNumToTime(segment.offduty.base),
                layoverDuration: ` (${formatNumToTime(segment.hotelSegment[0].totalLayover.value)}) `,
                layoverEndTime: formatNumToTime(pairingSegments[index+1].onduty.base),
                layoverPlace: segment.hotelSegment[0].hotel.name,
                layoverType: segment.hotelSegment[0].totalLayover.value >= 960 ? 'long' : 'short', 
                segmentLength: getSegmentLength(endDateRoute, layoverEndDate)
            })

            /* set the startDateRoute of the next segment to the layoverEndDate */
            startDateRoute = layoverEndDate;
        }
    })
    combineRoutes(segments);
    return segments;
}

const updatePairingCarryOutSegments = (segments: any[], sundays: string[], sundayIndex: number) => {
    const endOfWeekSundayDate = convertToDateObject(sundays[sundayIndex], "23:59");
    let updatedSegments = [] as any; 
    for(let i=0; i<segments.length; i++){
        if(segments[i].endDate.getTime() > endOfWeekSundayDate.getTime()) {
            updatedSegments.push({
                ...segments[i],
                endDate: new Date(endOfWeekSundayDate),
                segmentLength: getSegmentLength(segments[i].startDate, endOfWeekSundayDate),
                hideLayoverInformation: !segments[i].isRoute ? hideLayoverInformation(segments[i].startDate, endOfWeekSundayDate, segments[i].endDate, "carryOut") : null
            })
            break;
        } else updatedSegments.push(segments[i]);
    }
    return updatedSegments;  
}